import { createAdminAffiliateApi, createAdminProgrammingApi } from '@/services/admin-api/index';
import { createApiClient } from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import type { AuditLog, AuditLogEntityType } from '@ergshoot/shared';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Hook that provides admin API methods with proper Clerk token handling.
 * Uses refs and callbacks to ensure stable function references across renders.
 */
export function useAdminApi() {
  const { getToken } = useAuth();

  // Store getToken in a ref to avoid dependency changes
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Create a stable token getter that always uses the latest getToken
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const adminApi = useMemo(() => {
    const client = createApiClient(stableGetToken);

    const coreApi = {
      getDashboard: () =>
        client.get<{
          overview: {
            total_affiliates: number;
            total_agents: number;
            total_members: number;
            active_subscriptions: number;
          };
          growth: {
            new_affiliates_this_month: number;
            pending_approvals: number;
          };
          commissions: {
            pending_amount: number;
            paid_amount: number;
            pending_payouts: number;
          };
        }>('/admin/dashboard'),

      getAuditLogs: (params?: {
        limit?: number;
        cursor?: string;
        actor_user_id?: string;
        action?: string;
        entity_type?: AuditLogEntityType;
        entity_id?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        if (params?.cursor) queryParams.set('cursor', params.cursor);
        if (params?.actor_user_id) queryParams.set('actor_user_id', params.actor_user_id);
        if (params?.action) queryParams.set('action', params.action);
        if (params?.entity_type) queryParams.set('entity_type', params.entity_type);
        if (params?.entity_id) queryParams.set('entity_id', params.entity_id);
        const query = queryParams.toString();
        return client.get<{
          logs: AuditLog[];
          has_more: boolean;
          next_cursor: string | null;
        }>(`/admin/audit-logs${query ? `?${query}` : ''}`);
      },

      // User Management endpoints
      // Invite a new user
      inviteUser: (data: {
        email: string;
        name?: string;
        role: string;
        territory_name?: string;
        affiliate_name?: string;
        affiliate_id?: string;
      }) =>
        client.post<{
          user: {
            id: string;
            email: string;
            name: string;
            role: string;
            is_active: boolean;
          };
          message: string;
        }>('/admin/users/invite', data),

      bulkUploadUsers: (data: {
        users: Array<{ email: string; name?: string; role: string; affiliate_id?: string }>;
      }) =>
        client.post<{
          results: Array<{ email: string; status: 'created' | 'skipped'; reason?: string }>;
          summary: { total: number; created: number; skipped: number };
          message: string;
        }>('/admin/users/bulk-upload', data),

      // FEAT-APR08-006 — cross-gym tagged users (2+ gyms)
      getTaggedUsers: (params?: { limit?: number }) => {
        const qs = new URLSearchParams();
        if (params?.limit) qs.set('limit', params.limit.toString());
        const suffix = qs.toString() ? `?${qs}` : '';
        return client.get<{
          users: Array<{
            id: string;
            email: string;
            name: string;
            gym_count: number;
            gym_names: string[];
            roles_held: string[];
          }>;
          total: number;
        }>(`/admin/tagged-users${suffix}`);
      },

      getUsers: (params?: {
        role?: string;
        search?: string;
        cursor?: string;
        limit?: number;
        affiliate_id?: string;
        territory?: string;
        country_code?: string;
        sort_by?: string;
        sort_order?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.role) queryParams.set('role', params.role);
        if (params?.search) queryParams.set('search', params.search);
        if (params?.cursor) queryParams.set('cursor', params.cursor);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        if (params?.affiliate_id) queryParams.set('affiliate_id', params.affiliate_id);
        if (params?.territory) queryParams.set('territory', params.territory);
        if (params?.country_code) queryParams.set('country_code', params.country_code);
        if (params?.sort_by) queryParams.set('sort_by', params.sort_by);
        if (params?.sort_order) queryParams.set('sort_order', params.sort_order);
        const query = queryParams.toString();
        return client.get<{
          users: Array<{
            id: string;
            email: string;
            name: string;
            role: string;
            country_code: string | null;
            is_active: boolean;
            invite_status: 'invited' | 'active' | 'suspended';
            created_at: string;
            last_signed_in_at: string | null;
            // Profile-specific fields
            profile_id: string | null;
            profile_type: string | null;
            member_role: string | null;
            is_primary: boolean;
            agent?: {
              id: string;
              territory_name: string | null;
            };
            affiliate?: {
              id: string;
              name: string | null;
            };
          }>;
          counts: {
            total: number;
            admins: number;
            agents: number;
            affiliates: number;
            members: number;
            individuals: number;
          };
          pagination: {
            has_more: boolean;
            next_cursor: string | null;
          };
        }>(`/admin/users${query ? `?${query}` : ''}`);
      },

      // Resend invitation to a pending user
      resendInvite: (userId: string) =>
        client.post<{
          user: {
            id: string;
            email: string;
            name: string;
          };
          message: string;
        }>(`/admin/users/${userId}/resend-invite`),

      // Request password reset for an active user
      requestPasswordReset: (userId: string) =>
        client.post<{
          user: {
            id: string;
            email: string;
            name: string;
          };
          message: string;
        }>(`/admin/users/${userId}/request-password-reset`),

      // Assign user to a gym
      assignToGym: (
        userId: string,
        affiliateId: string,
        memberRole?: 'owner' | 'coach' | 'member'
      ) =>
        client.post<{
          member_id: string;
          profile_id: string;
          affiliate: { id: string; name: string };
          member_role: string;
          message: string;
        }>(`/admin/users/${userId}/assign-gym`, {
          affiliate_id: affiliateId,
          member_role: memberRole || 'member',
        }),

      // Remove user from a gym
      removeFromGym: (userId: string, affiliateId: string) =>
        client.delete<{
          message: string;
        }>(`/admin/users/${userId}/gym/${affiliateId}`),

      getAgents: (params?: { search?: string; cursor?: string; limit?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.set('search', params.search);
        if (params?.cursor) queryParams.set('cursor', params.cursor);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        const query = queryParams.toString();
        return client.get<{
          agents: Array<{
            id: string;
            user_id: string;
            name: string;
            email: string;
            country_code: string | null;
            is_active: boolean;
            territory_name: string | null;
            shopify_affiliate_code: string | null;
            commission_rates: {
              equipment: number | null;
              subscriptions: number | null;
            };
            affiliate_count: number;
            pending_commission: number;
            created_at: string;
          }>;
          pagination: {
            has_more: boolean;
            next_cursor: string | null;
          };
        }>(`/admin/agents${query ? `?${query}` : ''}`);
      },

      updateAgent: (
        agentId: string,
        data: {
          commission_rate_equipment?: number;
          commission_rate_subs?: number;
          territory_name?: string;
          shopify_affiliate_code?: string;
        }
      ) =>
        client.patch<{
          agent: {
            id: string;
            commission_rates: { equipment: number; subscriptions: number };
            territory_name: string | null;
            shopify_affiliate_code: string | null;
          };
        }>(`/admin/agents/${agentId}`, data),

      // User moderation endpoints
      suspendUser: (userId: string) =>
        client.patch<{
          user: {
            id: string;
            email: string;
            name: string;
            is_active: boolean;
          };
          message: string;
        }>(`/admin/users/${userId}/suspend`),

      activateUser: (userId: string) =>
        client.patch<{
          user: {
            id: string;
            email: string;
            name: string;
            is_active: boolean;
          };
          message: string;
        }>(`/admin/users/${userId}/activate`),

      // GDPR-compliant user deletion (FEAT-006)
      deleteUser: (userId: string) =>
        client.delete<{
          message: string;
        }>(`/admin/users/${userId}`),

      // Scores endpoints (HQ-005)
      getScores: (params?: {
        workout_id?: string;
        affiliate_id?: string;
        user_id?: string;
        is_verified?: boolean;
        cursor?: string;
        limit?: number;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.workout_id) queryParams.set('workout_id', params.workout_id);
        if (params?.affiliate_id) queryParams.set('affiliate_id', params.affiliate_id);
        if (params?.user_id) queryParams.set('user_id', params.user_id);
        if (params?.is_verified !== undefined)
          queryParams.set('is_verified', params.is_verified.toString());
        if (params?.cursor) queryParams.set('cursor', params.cursor);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        const query = queryParams.toString();
        return client.get<{
          scores: Array<{
            id: string;
            user: {
              id: string;
              name: string | null;
              email: string | null;
            };
            workout: {
              id: string;
              name: string | null;
              scoring_type: string | null;
              scoring_direction: string | null;
            };
            affiliate: {
              id: string;
              name: string | null;
            } | null;
            value: number;
            notes: string | null;
            is_verified: boolean;
            video_url: string | null;
            created_at: string;
            updated_at: string;
          }>;
          pagination: {
            has_more: boolean;
            next_cursor: string | null;
          };
        }>(`/admin/scores${query ? `?${query}` : ''}`);
      },

      verifyScore: (scoreId: string, isVerified: boolean) =>
        client.patch<{
          id: string;
          is_verified: boolean;
          message: string;
        }>(`/admin/scores/${scoreId}/verify`, { is_verified: isVerified }),

      // Track CRUD endpoints
      createTrack: (data: {
        name: string;
        description?: string;
        access_level?: string;
        is_coming_soon?: boolean;
      }) =>
        client.post<{
          track: {
            id: string;
            name: string;
            description: string | null;
            is_active: boolean;
            is_coming_soon: boolean;
            logo_url: string | null;
          };
          message: string;
        }>('/admin/tracks', data),

      updateTrack: (
        trackId: string,
        data: {
          name?: string;
          description?: string;
          is_active?: boolean;
          access_level?: string;
          is_coming_soon?: boolean;
          logo_url?: string;
        }
      ) =>
        client.patch<{
          track: {
            id: string;
            name: string;
            description: string | null;
            is_active: boolean;
            is_coming_soon: boolean;
            logo_url: string | null;
          };
          message: string;
        }>(`/admin/tracks/${trackId}`, data),

      uploadTrackLogo: (trackId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return client.upload<{
          track: {
            id: string;
            name: string;
            logo_url: string;
          };
          message: string;
        }>(`/admin/tracks/${trackId}/logo`, formData);
      },

      // Permissions endpoints (FEAT-007) - Base44 aligned
      getPermissions: () =>
        client.get<{
          permissions: Array<{
            key: string;
            label: string;
            description: string;
            comingSoon: boolean;
            roles: Record<string, boolean>;
          }>;
          roles: Array<{
            key: string;
            label: string;
          }>;
          stats: {
            totalPermissions: number;
            totalRoles: number;
            comingSoonCount: number;
          };
        }>('/admin/permissions'),

      updatePermission: (data: { role: string; permission_key: string; is_enabled: boolean }) =>
        client.patch<{
          role: string;
          permission_key: string;
          is_enabled: boolean;
          message: string;
        }>('/admin/permissions', data),

      updatePermissions: (
        changes: Array<{ role: string; permission_key: string; is_enabled: boolean }>
      ) =>
        client.patch<{
          updated_count: number;
          message: string;
        }>('/admin/permissions/bulk', { changes }),

      resetPermissions: () =>
        client.post<{
          message: string;
        }>('/admin/permissions/reset'),

      // Events endpoints (admin sees all events across affiliates)
      getEvents: (params?: {
        status?: string;
        affiliate_id?: string;
        visibility?: string;
        search?: string;
        date_from?: string;
        date_to?: string;
        cursor?: string;
        limit?: number;
      }) => {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.append('status', params.status);
        if (params?.affiliate_id) searchParams.append('affiliate_id', params.affiliate_id);
        if (params?.visibility) searchParams.append('visibility', params.visibility);
        if (params?.search) searchParams.append('search', params.search);
        if (params?.date_from) searchParams.append('date_from', params.date_from);
        if (params?.date_to) searchParams.append('date_to', params.date_to);
        if (params?.cursor) searchParams.append('cursor', params.cursor);
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        const query = searchParams.toString();
        return client.get<{
          events: Array<{
            id: string;
            name: string;
            description: string | null;
            event_date: string;
            start_time: string | null;
            end_time: string | null;
            event_timezone: string | null;
            start_at_utc: string | null;
            end_at_utc: string | null;
            format: string | null;
            max_participants: number | null;
            status: string;
            visibility: 'internal' | 'public';
            affiliate_id: string | null;
            affiliate_name: string | null;
            workout_id: string | null;
            workout_title: string | null;
            participant_count: number;
            is_registered: boolean;
            spots_remaining: number | null;
          }>;
          pagination: { has_more: boolean; next_cursor: string | null };
        }>(`/events${query ? `?${query}` : ''}`);
      },

      getEventParticipants: (eventId: string) =>
        client.get<{
          participants: Array<{
            id: string;
            user_id: string;
            user_name: string;
            user_email: string;
            signed_up_at: string;
          }>;
          total: number;
        }>(`/events/${eventId}/participants`),

      removeEventParticipant: (eventId: string, participantId: string, idempotencyKey?: string) =>
        client.delete<{
          participant_id: string;
          participant_count: number;
        }>(`/events/${eventId}/participants/${participantId}`, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      getEventWorkouts: () =>
        client.get<{
          items: Array<{
            id: string;
            name: string;
            scheduled_date: string | null;
            affiliate_id: string | null;
            source: string | null;
            visibility: string | null;
            shared_to_global: number;
          }>;
        }>('/events/workouts'),

      createEvent: (
        data: {
          name: string;
          description?: string;
          event_date: string;
          start_time?: string;
          end_time?: string;
          event_timezone?: string;
          format?: string;
          workout_id?: string;
          max_participants?: number;
          affiliate_id?: string;
          visibility?: 'internal' | 'public';
        },
        idempotencyKey?: string
      ) =>
        client.post<{
          event_id: string;
          message: string;
        }>('/events', data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      updateEvent: (
        id: string,
        data: {
          name?: string;
          description?: string | null;
          event_date?: string;
          start_time?: string | null;
          end_time?: string | null;
          event_timezone?: string | null;
          format?: string | null;
          workout_id?: string | null;
          max_participants?: number | null;
          status?: string;
          visibility?: 'internal' | 'public';
        },
        idempotencyKey?: string
      ) =>
        client.put<{
          message: string;
        }>(`/events/${id}`, data, {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        }),

      deleteEvent: (id: string) =>
        client.delete<{
          message: string;
        }>(`/events/${id}`),

      // Territory Management endpoints (FEAT-TERRITORY-001)
      getTerritories: () =>
        client.get<{
          territories: Array<{
            id: string;
            name: string;
            country_code: string | null;
            region: string | null;
            status: 'locked' | 'unlocked' | 'assigned';
            unlocked_at: string | null;
            assigned_at: string | null;
            notes: string | null;
            created_at: string;
            updated_at: string;
            agent_id: string | null;
            agent_name: string | null;
            agent_email: string | null;
            affiliate_count: number;
          }>;
          stats: {
            total: number;
            locked: number;
            unlocked: number;
            assigned: number;
          };
        }>('/admin/territories'),

      getTerritory: (territoryId: string) =>
        client.get<{
          territory: {
            id: string;
            name: string;
            country_code: string | null;
            region: string | null;
            status: 'locked' | 'unlocked' | 'assigned';
            unlocked_at: string | null;
            assigned_at: string | null;
            notes: string | null;
            created_at: string;
            updated_at: string;
            agent_id: string | null;
            agent_name: string | null;
            agent_email: string | null;
            affiliate_count: number;
          };
        }>(`/admin/territories/${territoryId}`),

      createTerritory: (data: {
        name: string;
        country_code?: string;
        region?: string;
        status?: 'locked' | 'unlocked' | 'assigned';
        notes?: string;
      }) =>
        client.post<{
          territory: {
            id: string;
            name: string;
            country_code: string | null;
            region: string | null;
            status: string;
          };
          message: string;
        }>('/admin/territories', data),

      updateTerritory: (
        territoryId: string,
        data: {
          name?: string;
          country_code?: string;
          region?: string;
          status?: 'locked' | 'unlocked' | 'assigned';
          notes?: string;
        }
      ) =>
        client.patch<{
          message: string;
        }>(`/admin/territories/${territoryId}`, data),

      unlockTerritory: (territoryId: string) =>
        client.post<{
          message: string;
        }>(`/admin/territories/${territoryId}/unlock`),

      lockTerritory: (territoryId: string) =>
        client.post<{
          message: string;
        }>(`/admin/territories/${territoryId}/lock`),

      assignTerritory: (territoryId: string, agentId: string) =>
        client.post<{
          message: string;
        }>(`/admin/territories/${territoryId}/assign`, { agent_id: agentId }),

      unassignTerritory: (territoryId: string) =>
        client.post<{
          message: string;
        }>(`/admin/territories/${territoryId}/unassign`),

      deleteTerritory: (territoryId: string) =>
        client.delete<{
          message: string;
        }>(`/admin/territories/${territoryId}`),

      // Activity Feed (HQ-002)
      getActivityFeed: (params?: { limit?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        const query = queryParams.toString();
        return client.get<{
          activities: Array<{
            type: 'user_signup' | 'affiliate_update' | 'score_logged';
            id: string;
            description: string;
            timestamp: string;
            meta?: Record<string, string | number | boolean | null>;
          }>;
        }>(`/admin/activity-feed${query ? `?${query}` : ''}`);
      },

      // User Profile Management (HQ-012)
      getUserDetail: (userId: string) =>
        client.get<{
          user: {
            id: string;
            email: string;
            name: string;
            role: string;
            country_code: string | null;
            phone: string | null;
            is_active: boolean;
            active_profile_id: string | null;
            created_at: string;
            last_signed_in_at: string | null;
          };
          profiles: Array<{
            id: string;
            profile_type: string;
            member_role: string | null;
            is_primary: boolean;
            is_active: boolean;
            affiliate_id: string | null;
            agent_id: string | null;
            individual_id: string | null;
            created_at: string;
            affiliate_name: string | null;
            territory_name: string | null;
          }>;
          agent_details: {
            id: string;
            commission_rate_equipment: number | null;
            commission_rate_subs: number | null;
            territory_name: string | null;
          } | null;
          payment_details: {
            stripe_customer_id: string | null;
            subscription_status: string | null;
          } | null;
        }>(`/admin/users/${userId}`),

      updateUser: (userId: string, data: { phone?: string }) =>
        client.patch<{
          message: string;
        }>(`/admin/users/${userId}`, data),

      addUserProfile: (
        userId: string,
        data: {
          profile_type: string;
          territory_name?: string;
          affiliate_id?: string;
          member_role?: string;
        }
      ) =>
        client.post<{
          profile_id: string;
          profile_type: string;
          is_primary: boolean;
          message: string;
        }>(`/admin/users/${userId}/profiles`, data),

      removeUserProfile: (userId: string, profileId: string) =>
        client.delete<{
          message: string;
        }>(`/admin/users/${userId}/profiles/${profileId}`),

      setUserPrimaryProfile: (userId: string, profileId: string) =>
        client.patch<{
          message: string;
        }>(`/admin/users/${userId}/profiles/${profileId}/primary`),

      reactivateUserProfile: (userId: string, profileId: string) =>
        client.patch<{
          message: string;
        }>(`/admin/users/${userId}/profiles/${profileId}/reactivate`),

      // Subscriptions management (NF-1)
      getSubscriptions: (params?: {
        status?: string;
        search?: string;
        type?: string;
        limit?: number;
        offset?: number;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.set('status', params.status);
        if (params?.search) queryParams.set('search', params.search);
        if (params?.type) queryParams.set('type', params.type);
        if (params?.limit) queryParams.set('limit', params.limit.toString());
        if (params?.offset) queryParams.set('offset', params.offset.toString());
        const query = queryParams.toString();
        return client.get<{
          subscriptions: Array<{
            id: string;
            name: string | null;
            email: string | null;
            type: 'affiliate' | 'individual';
            plan_name: string;
            status: string;
            stripe_customer_id: string | null;
            is_paused: number;
            pause_resumes_at: string | null;
            created_at: string;
          }>;
          total: number;
          limit: number;
          offset: number;
        }>(`/admin/subscriptions${query ? `?${query}` : ''}`);
      },
    };

    return {
      ...coreApi,
      ...createAdminAffiliateApi(client),
      ...createAdminProgrammingApi(client),
    };
  }, [stableGetToken]);

  return adminApi;
}
